const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/MaskedTextField/MaskedTextField.tsx';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import React, { forwardRef, useEffect, useState } from 'react';

// 9 = Ziffer (0-9)
// x = Buchstabe (A-Z, a-z)
// * = alphanumerisch
type MaskChar = '9' | 'x' | '*';

const DEFAULT_MASK_CHARS: Record<MaskChar, string> = {
    9: '[0-9]',
    x: '[A-Za-z]',
    '*': '[A-Za-z0-9]',
};

export interface MaskedTextFieldProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
    mask: string;
    name?: string;
    value?: string;
    onMaskedChange?: (raw: string, masked: string) => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    readOnly?: boolean;
    className?: string;
}

export const MaskedTextField = forwardRef<HTMLInputElement, MaskedTextFieldProps>(
    (
        {
            name = undefined,
            mask,
            onMaskedChange,
            value: externalValue,
            onChange,
            error,
            helperText,
            readOnly,
            className,
            ...restProps
        },
        ref
    ) => {
        const [inner, setInner] = useState((externalValue as string) ?? '');
        const prefix = mask.match(/^[^9x*]*/)?.[0] ?? '';
        const placeholder = mask.replace(/(9|x|\*)/g, '_');

        const applyMask = (input: string): string => {
            // The formatted result will be built here
            let clean = '';

            // Tracks the current position within the mask
            let maskIndex = 0;

            // Loop through every character in the input string
            for (const char of input) {
                // Stop if we’ve reached the end of the mask
                if (maskIndex >= mask.length) break;

                // Get the current mask character
                const maskChar = mask[maskIndex];

                // Check if this mask character is a placeholder (defined in DEFAULT_MASK_CHARS)
                if (DEFAULT_MASK_CHARS[maskChar as MaskChar]) {
                    // Create a regex to test allowed input for this placeholder
                    const allowed = new RegExp(DEFAULT_MASK_CHARS[maskChar as MaskChar]);

                    // If the input character matches the placeholder pattern, append it
                    if (allowed.test(char)) {
                        clean += char;
                        maskIndex++;
                    }
                } else {
                    // The mask character is a literal
                    if (char === maskChar) {
                        // If the user typed the literal themselves, just add it
                        clean += char;
                        maskIndex++;
                    } else {
                        // Otherwise, automatically insert the literal character
                        clean += maskChar;
                        maskIndex++;

                        // Check the next mask character to see if it's a placeholder
                        const nextMaskChar = mask[maskIndex];
                        const reRun = new RegExp(DEFAULT_MASK_CHARS[nextMaskChar as MaskChar] || '.');

                        // If the current input char matches the next placeholder, use it
                        if (reRun.test(char)) {
                            clean += char;
                            maskIndex++;
                        }
                    }
                }
            }

            // Return the formatted string
            return clean;
        };

        const value = applyMask(inner);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let inputValue = e.target.value;
            if (inputValue.length < prefix.length) {
                inputValue = prefix;
            } else if (!inputValue.startsWith(prefix)) {
                inputValue = prefix + inputValue.slice(prefix.length);
            }

            const masked = applyMask(inputValue);

            if (masked !== value) {
                setInner(masked);
                onMaskedChange?.(masked.replace(/[^A-Za-z0-9]/g, ''), masked);
                onChange?.({ ...e, target: { ...e.target, value: masked } });
            }
        };

        useEffect(() => {
            if (externalValue !== undefined && externalValue !== value) {
                setInner(externalValue as string);
            }
        }, [externalValue]);

        return (
            <TextField
                {...restProps}
                name={name}
                ref={ref}
                value={value || prefix}
                className={className}
                onChange={handleChange}
                slotProps={{ htmlInput: { maxLength: mask.length } }}
                placeholder={placeholder}
                error={!!error}
                helperText={helperText}
                disabled={readOnly}
            />
        );
    }
);
